import {
  Box,
  CameraAltOutlinedIcon,
  CloseIcon,
  Flex,
  IconButton,
  Image,
  Text,
  useTheme,
} from '@shared/components';
import { useController } from 'react-hook-form';

export interface FormImageInputProps {
  name: string;
  label?: string;
  required?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>,
    reason: 'input' | 'preview',
  ) => void;
}

export default function FormImageInput(props: Readonly<FormImageInputProps>) {
  const { label, required, onClick } = props;

  const {
    field: { ref, disabled, value, onChange },
    fieldState: { error },
  } = useController({ ...props, name: props.name });

  const theme = useTheme();

  const handleResetImage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onChange({ ...event, target: { value: '' } });
  };

  const isError = !!error;
  const helperText = error?.message ?? '';

  return (
    <Flex
      sx={{
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(1),
      }}
    >
      {/* Label */}
      {label ? (
        <Flex sx={{ gap: (theme) => theme.spacing(1) }}>
          <Text variant='body2Semibold'>{label}</Text>
          {required ? (
            <Text variant='body2Semibold' color='error'>
              *
            </Text>
          ) : null}
        </Flex>
      ) : null}

      {/* Form field */}
      {!value ? (
        // Input state
        <IconButton
          sx={(theme) => ({
            width: '200px',
            height: '200px',
            borderRadius: theme.shape.rounded(2),
            backgroundColor: theme.palette.grey[50],
            border: `1px dashed ${!isError ? theme.palette.grey[300] : theme.palette.error.main}`,
            '.MuiSvgIcon-root': {
              fontSize: '32px',
            },
          })}
          disableRipple
          ref={ref}
          onClick={(e) => {
            onClick?.(e, 'input');
          }}
          disabled={disabled}
        >
          <CameraAltOutlinedIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
        </IconButton>
      ) : (
        // Preview state
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e, 'preview');
          }}
          sx={(theme) => ({
            width: '200px',
            height: '200px',
            position: 'relative',
            borderRadius: theme.shape.rounded(2),
            cursor: 'pointer',
          })}
        >
          <Image
            src={value}
            alt='Captured frame'
            style={{
              width: '200px',
              height: '200px',
              display: 'block',
              objectFit: 'cover',
              borderRadius: theme.spacing(2),
            }}
          />
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleResetImage(e);
            }}
            sx={(theme) => ({
              p: theme.spacing(1),
              backgroundColor: theme.palette.grey[100],
              position: 'absolute',
              right: '8px',
              top: '8px',
              '.MuiSvgIcon-root': {
                fontSize: '20px',
              },
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Helper text */}
      {isError ? (
        <Text variant='caption' color='error'>
          {helperText}
        </Text>
      ) : null}
    </Flex>
  );
}
